/**
 * Shutdown trace utilities — prove which resources remain alive after call end.
 */

export function createShutdownId(callId, source) {
  const safeCallId = String(callId || "unknown");
  const safeSource = String(source || "unknown");
  return `${safeCallId}:${safeSource}:${Date.now()}`;
}

export function shutdownAuditStart(shutdownId, source, platform = "web") {
  console.log(`[shutdownAudit] start shutdownId=${shutdownId} source=${source} platform=${platform}`);
}

export function shutdownAuditStep(shutdownId, step, ok = true, extra = {}, platform = "web") {
  const status = ok ? "ok" : "fail";
  const tag = platform === "native" ? "[shutdownAudit:native]" : "[shutdownAudit:web]";
  if (extra && Object.keys(extra).length > 0) {
    console.log(`${tag} step=${step} ${status} shutdownId=${shutdownId}`, extra);
  } else {
    console.log(`${tag} step=${step} ${status} shutdownId=${shutdownId}`);
  }
}

export function shutdownAuditDone(shutdownId, platform = "web") {
  console.log(`[shutdownAudit] done shutdownId=${shutdownId} platform=${platform}`);
}

export function shutdownAuditError(message, extra = {}) {
  console.error(`[shutdownAudit] ERROR ${message}`, extra);
}

export function scheduleWebPostCleanupChecks(shutdownId, getState, callId = null) {
  [500, 1500, 3000].forEach((delayMs) => {
    window.setTimeout(() => {
      const state = getState();
      console.log(
        `[shutdownAudit:web] postCleanupCheck delayMs=${delayMs} shutdownId=${shutdownId}`,
        state
      );
      const tracksOk =
        state.localVideoTracksEnded !== false &&
        state.localAudioTracksEnded !== false &&
        state.remoteTracksEnded !== false;
      const elementsOk = state.remoteElementsCleared !== false;
      const engineOk = state.zegoEngineNull !== false;
        if (!tracksOk || !elementsOk || !engineOk) {
          shutdownAuditError("active call state remains after shutdown", {
            shutdownId,
            delayMs,
            callId,
            ...state,
          });
          if (callId) {
            import("./webCallMediaRegistry")
              .then(({ logRemainingRegistryResources }) => {
                logRemainingRegistryResources(callId, shutdownId);
              })
              .catch((err) => {
                console.warn("[shutdownAudit:web] logRemainingRegistryResources failed", err);
              });
          }
        }
    }, delayMs);
  });
}

export function auditStopMediaStreamTracks(stream, shutdownId, label = "stream") {
  if (!stream?.getTracks) {
    shutdownAuditStep(shutdownId, `stop_${label}_tracks_skipped`, true, { reason: "no_tracks" });
    return;
  }
  stream.getTracks().forEach((track) => {
    const kind = track?.kind || "unknown";
    const beforeState = track?.readyState || "unknown";
    try {
      track.stop();
    } catch (e) {
      shutdownAuditStep(
        shutdownId,
        `stop_${label}_track kind=${kind}`,
        false,
        { readyState: beforeState, error: e?.message || e }
      );
      return;
    }
    shutdownAuditStep(
      shutdownId,
      `stop_${label}_track kind=${kind} readyState=${track?.readyState || beforeState}`,
      true
    );
    console.log(
      `[shutdownAudit:web] stop ${label} track kind=${kind} readyState=${track?.readyState || beforeState}`
    );
  });
}

export function auditClearVideoElements(containerRef, shutdownId, label) {
  const container = containerRef?.current;
  if (!container) {
    shutdownAuditStep(shutdownId, `clear_${label}_element_skipped`, true, { reason: "no_container" });
    return;
  }

  const videos =
    container.tagName === "VIDEO"
      ? [container]
      : Array.from(container.querySelectorAll("video"));

  videos.forEach((video, index) => {
    const hadSrc = !!video.srcObject;
    try {
      video.srcObject?.getTracks?.().forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
      });
      video.srcObject = null;
      video.pause?.();
    } catch (e) {
      shutdownAuditStep(shutdownId, `clear_${label}_element index=${index}`, false, {
        error: e?.message || e,
      });
      return;
    }
    shutdownAuditStep(shutdownId, `clear_${label}_element index=${index}`, true, { hadSrc });
  });
  console.log(`[shutdownAudit:web] clear video element srcObject done label=${label}`);
}

export function getWebCleanupStateSnapshot({
  localStreamRef,
  remoteStreamRef,
  localVideoRef,
  remoteVideoRef,
  zegoRef,
  heartbeatRef,
  timerRef,
  engineStartedRef,
  mediaCleanedRef,
}) {
  const localTracks = localStreamRef?.current?.getTracks?.() || [];
  const remoteTracks = remoteStreamRef?.current?.getTracks?.() || [];
  const localVideoEnded = localTracks
    .filter((t) => t.kind === "video")
    .every((t) => t.readyState === "ended");
  const localAudioEnded = localTracks
    .filter((t) => t.kind === "audio")
    .every((t) => t.readyState === "ended");

  const hasRemoteSrc = [localVideoRef, remoteVideoRef].some((ref) => {
    const el = ref?.current;
    if (!el) return false;
    if (el.tagName === "VIDEO") return !!el.srcObject;
    return Array.from(el.querySelectorAll("video")).some((v) => !!v.srcObject);
  });

  return {
    localVideoTracksEnded: localTracks.length === 0 || localVideoEnded,
    localAudioTracksEnded: localTracks.length === 0 || localAudioEnded,
    remoteElementsCleared: !hasRemoteSrc,
    remoteTracksEnded:
      remoteTracks.length === 0 || remoteTracks.every((t) => t.readyState === "ended"),
    zegoEngineNull: !zegoRef?.current,
    heartbeatCleared: !heartbeatRef?.current,
    timerCleared: !timerRef?.current,
    engineStarted: !!engineStartedRef?.current,
    mediaCleaned: !!mediaCleanedRef?.current,
  };
}
